import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { SectionHeading, FadeIn } from "@/components/laa/Sections";
import { FASHION_DETECTIVE_URL } from "@/data/siteData";
import { UploadCloud, X, CheckCircle2, Loader2, ArrowUpRight, BadgeCheck, ShieldCheck, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_PHOTOS = 5;
const MAX_PHOTO_BYTES = 3 * 1024 * 1024;
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];

const inputCls =
  "bg-[rgba(244,239,230,0.04)] border-[color:var(--laa-hairline)] text-[color:var(--laa-ivory)] placeholder:text-[rgba(244,239,230,0.35)] focus-visible:ring-[color:var(--laa-champagne)] rounded-xl h-11";
const labelCls = "text-xs tracking-[0.14em] uppercase text-[rgba(244,239,230,0.6)]";

const FieldError = ({ msg, testid }) =>
  msg ? (
    <p data-testid={testid} className="mt-1.5 text-xs text-red-400">
      {msg}
    </p>
  ) : null;

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

/* ---------------- APPRAISAL REDIRECT CTA ---------------- */

export const AppraisalRedirectSection = () => (
  <section
    id="request"
    data-testid="appraisal-redirect-section"
    className="py-16 sm:py-20 lg:py-24 border-t border-[color:var(--laa-hairline)] bg-[color:var(--laa-ink-2)] relative noise overflow-hidden"
  >
    <div className="absolute inset-0 bg-[radial-gradient(50%_60%_at_50%_0%,rgba(184,154,99,0.08),transparent_60%)]" />
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <div className="max-w-3xl mx-auto text-center">
        <FadeIn>
          <p className="laa-label">Begin Your Request</p>
          <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl leading-[1.1] tracking-[-0.01em] text-[color:var(--laa-ivory)]">
            Request an appraisal or authentication.
          </h2>
          <p className="mt-5 text-sm sm:text-base leading-relaxed text-[rgba(244,239,230,0.65)]">
            All appraisal and authentication requests are handled through our dedicated platform,
            The Fashion Detective — fast, secure, and expert-reviewed.
          </p>
        </FadeIn>
        <FadeIn delay={0.1}>
          <div className="mt-9">
            <a href={FASHION_DETECTIVE_URL} target="_blank" rel="noopener noreferrer">
              <Button
                data-testid="appraisal-redirect-button"
                className="rounded-xl px-8 py-6 text-sm font-medium bg-[color:var(--laa-ivory)] text-[color:var(--laa-ink)] hover:bg-white focus-visible:ring-2 focus-visible:ring-[color:var(--laa-champagne)]"
              >
                Start on TheFashionDetective.com
                <ArrowUpRight size={16} className="ml-2" />
              </Button>
            </a>
          </div>
        </FadeIn>
        <FadeIn delay={0.18}>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
            {[
              { icon: Sparkles, text: "Fast turnaround" },
              { icon: BadgeCheck, text: "Expert-reviewed" },
              { icon: ShieldCheck, text: "Formal documentation" },
            ].map(({ icon: Icon, text }) => (
              <span key={text} className="flex items-center gap-2 text-xs tracking-[0.14em] uppercase text-[rgba(244,239,230,0.55)]">
                <Icon size={14} className="text-[color:var(--laa-champagne)]" />
                {text}
              </span>
            ))}
          </div>
          <p className="mt-8 text-xs text-[rgba(244,239,230,0.45)]">
            Prefer to reach us directly? Use the{" "}
            <a href="#contact" data-testid="appraisal-redirect-contact-link" className="laa-link text-[color:var(--laa-champagne)]">
              contact form below
            </a>{" "}
            — photos welcome.
          </p>
        </FadeIn>
      </div>
    </div>
  </section>
);

/* ---------------- CONTACT (with photo attachments) ---------------- */

export const ContactSection = ({ prefillMessage }) => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [photos, setPhotos] = useState([]); // {filename, content_type, data, preview}
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const fileRef = useRef(null);

  // Apply prefill coming from category cards
  const appliedPrefill = useRef(null);
  if (prefillMessage && appliedPrefill.current !== prefillMessage) {
    appliedPrefill.current = prefillMessage;
    if (form.message !== prefillMessage) {
      setForm((f) => ({ ...f, message: prefillMessage }));
    }
  }

  const set = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const room = MAX_PHOTOS - photos.length;
    if (files.length > room) {
      toast.error(`You can attach a maximum of ${MAX_PHOTOS} photos.`);
    }
    const accepted = [];
    for (const file of files.slice(0, room)) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error(`${file.name}: only JPG, PNG, or WEBP images are accepted.`);
        continue;
      }
      if (file.size > MAX_PHOTO_BYTES) {
        toast.error(`${file.name}: each photo must be under 3MB.`);
        continue;
      }
      const data = await fileToBase64(file);
      accepted.push({
        filename: file.name,
        content_type: file.type,
        data,
        preview: URL.createObjectURL(file),
      });
    }
    if (accepted.length) setPhotos((p) => [...p, ...accepted]);
  };

  const removePhoto = (idx) => setPhotos((p) => p.filter((_, i) => i !== idx));

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = "Please enter your name.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) er.email = "Please enter a valid email address.";
    if (!form.message.trim()) er.message = "Please enter a message.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please review the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        photos: photos.map(({ filename, content_type, data }) => ({ filename, content_type, data })),
      };
      const res = await axios.post(`${API}/contact`, payload);
      setSuccess(res.data);
      toast.success("Message sent. We'll be in touch shortly.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : typeof detail === "string" ? detail : "Unable to send your message. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSuccess(null);
    setForm({ name: "", email: "", message: "" });
    setPhotos([]);
    appliedPrefill.current = null;
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-16 sm:py-20 lg:py-24 border-t border-[color:var(--laa-hairline)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <SectionHeading
              label="Contact"
              title="Speak with the expert directly."
              sub="Questions about appraisals, authentication, expert witness services, or something else entirely — we answer every message personally. Attach photos of your item if helpful."
            />
            <FadeIn delay={0.12}>
              <div className="mt-8 rounded-2xl border border-[color:var(--laa-hairline)] bg-[rgba(20,21,23,0.55)] p-6">
                <p className="laa-label">What Happens Next</p>
                <ol className="mt-4 space-y-3">
                  {[
                    "We personally review your message and photographs.",
                    "You receive a reply within one business day.",
                    "If your item needs formal review, we guide you to the right service.",
                  ].map((c, i) => (
                    <li key={i} className="flex gap-3 text-xs leading-relaxed text-[rgba(244,239,230,0.6)]">
                      <span className="font-serif-display text-base text-[color:var(--laa-champagne)] leading-none">{String(i + 1).padStart(2, "0")}</span>
                      {c}
                    </li>
                  ))}
                </ol>
              </div>
            </FadeIn>
          </div>
          <FadeIn delay={0.1} className="lg:col-span-7">
            {success ? (
              <div data-testid="contact-success-message" className="rounded-2xl border border-[color:var(--laa-hairline)] bg-[rgba(20,21,23,0.55)] p-10 text-center">
                <CheckCircle2 size={36} className="mx-auto text-[color:var(--laa-champagne)]" />
                <h3 className="mt-5 text-xl text-[color:var(--laa-ivory)]">Thank you — your message has been sent.</h3>
                <p className="mt-3 text-sm text-[rgba(244,239,230,0.6)]">
                  Your reference number is{" "}
                  <span data-testid="contact-reference" className="text-[color:var(--laa-champagne)] font-medium tracking-wider">
                    {success.reference}
                  </span>
                  . We typically respond within one business day.
                </p>
                <Button
                  data-testid="contact-new-message-button"
                  onClick={resetForm}
                  variant="outline"
                  className="mt-7 rounded-xl bg-transparent border-[color:var(--laa-hairline)] text-[color:var(--laa-ivory)] hover:bg-[rgba(244,239,230,0.05)] hover:text-[color:var(--laa-ivory)]"
                >
                  Send another message
                </Button>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={submit} className="rounded-2xl border border-[color:var(--laa-hairline)] bg-[rgba(20,21,23,0.55)] p-6 sm:p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label className={labelCls}>Name *</Label>
                    <Input data-testid="contact-name-input" value={form.name} onChange={set("name")} placeholder="Full name" className={`mt-2 ${inputCls}`} />
                    <FieldError msg={errors.name} testid="contact-name-error" />
                  </div>
                  <div>
                    <Label className={labelCls}>Email *</Label>
                    <Input data-testid="contact-email-input" value={form.email} onChange={set("email")} placeholder="you@example.com" className={`mt-2 ${inputCls}`} />
                    <FieldError msg={errors.email} testid="contact-email-error" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className={labelCls}>Message *</Label>
                    <Textarea
                      data-testid="contact-message-input"
                      value={form.message}
                      onChange={set("message")}
                      placeholder="How can we help? Include brand, item, and any details."
                      className={`mt-2 min-h-[120px] ${inputCls} h-auto`}
                    />
                    <FieldError msg={errors.message} testid="contact-message-error" />
                  </div>
                </div>

                {/* Photo attachments */}
                <div className="mt-6">
                  <Label className={labelCls}>Photos (optional)</Label>
                  <button
                    type="button"
                    data-testid="contact-photo-dropzone"
                    onClick={() => fileRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      handleFiles(e.dataTransfer.files);
                    }}
                    className="mt-2 w-full rounded-xl border border-dashed border-[rgba(244,239,230,0.25)] bg-[rgba(244,239,230,0.03)] p-7 text-center hover:border-[color:var(--laa-champagne)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--laa-champagne)]"
                    style={{ transition: "border-color 200ms" }}
                  >
                    <UploadCloud size={22} className="mx-auto text-[color:var(--laa-champagne)]" />
                    <p className="mt-3 text-sm text-[rgba(244,239,230,0.7)]">Click or drag photos here</p>
                    <p className="mt-1 text-xs text-[rgba(244,239,230,0.4)]">Up to {MAX_PHOTOS} images · JPG, PNG, WEBP · max 3MB each</p>
                  </button>
                  <input
                    ref={fileRef}
                    data-testid="contact-photo-upload-input"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      handleFiles(e.target.files);
                      e.target.value = "";
                    }}
                  />
                  {photos.length > 0 && (
                    <div data-testid="contact-photo-previews" className="mt-4 grid grid-cols-3 sm:grid-cols-5 gap-3">
                      {photos.map((p, i) => (
                        <div key={`${p.filename}-${i}`} className="relative group rounded-lg overflow-hidden border border-[color:var(--laa-hairline)]">
                          <img src={p.preview} alt={p.filename} className="w-full h-20 object-cover" />
                          <button
                            type="button"
                            data-testid={`contact-photo-remove-${i}`}
                            onClick={() => removePhoto(i)}
                            aria-label={`Remove ${p.filename}`}
                            className="absolute top-1 right-1 rounded-full bg-[rgba(14,15,16,0.8)] p-1 text-[color:var(--laa-ivory)] hover:text-red-400"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-form-submit-button"
                  className="mt-7 rounded-xl px-8 py-5 text-sm font-medium bg-[color:var(--laa-ivory)] text-[color:var(--laa-ink)] hover:bg-white disabled:opacity-60"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <Loader2 size={16} className="animate-spin" /> Sending…
                    </span>
                  ) : (
                    "Send Message"
                  )}
                </Button>
              </form>
            )}
          </FadeIn>
        </div>
      </div>
    </section>
  );
};
